import { type EIP1193Provider } from '@web3-onboard/core'
import { JsonRpcProvider, Web3Provider, type Network } from '@ethersproject/providers'
import ExternalStore from '@/services/ExternalStore'
import { EMPTY_DATA } from '@safe-global/protocol-kit/dist/src/utils/constants'
import { type MulticallProvider } from 'ethers-multicall-provider'

type ReadOnlyProvider = JsonRpcProvider | Web3Provider

export const asUnbatchedWeb3ReadOnly = <TProvider extends ReadOnlyProvider>(
  provider: TProvider,
): MulticallProvider<TProvider> => {
  // SAFE4 has no Multicall3 deployment. Keep the store's existing type contract while
  // deliberately using the underlying provider for individual eth_call requests.
  return provider as unknown as MulticallProvider<TProvider>
}

export const createWeb3ReadOnly = (customRpc: string): JsonRpcProvider => {
  return new JsonRpcProvider({ url: customRpc, timeout: 10_000 })
}

export const createMultiWeb3ReadOnly = (customRpc: string, network: Network): MulticallProvider<JsonRpcProvider> => {
  // SAFE4 patch: ethers-multicall-provider hardcodes the canonical Multicall3 address
  // (0xcA11…CA11), which is not deployed on SAFE4, so batched reads (tryAggregate) revert
  // with "Error loading Safe owners". Bypass multicall and use the plain provider (individual
  // eth_call per read). Registered chains would lose batching, but this deployment only ever
  // serves SAFE4; correctness over batching.
  return asUnbatchedWeb3ReadOnly(new JsonRpcProvider(customRpc, network))
}

export const createWeb3 = (walletProvider: EIP1193Provider): Web3Provider => {
  return new Web3Provider(walletProvider)
}

export const { setStore: setWeb3, useStore: useWeb3 } = new ExternalStore<Web3Provider>()

export const createSafeAppsWeb3Provider = (safeAppsRpcUri: string): JsonRpcProvider | undefined => {
  return new JsonRpcProvider({ url: safeAppsRpcUri, timeout: 10_000 })
}

export const {
  getStore: getWeb3ReadOnly,
  setStore: setWeb3ReadOnly,
  useStore: useWeb3ReadOnly,
} = new ExternalStore<JsonRpcProvider>()

export const {
  getStore: getMultiWeb3ReadOnly,
  setStore: setMultiWeb3ReadOnly,
  useStore: useMultiWeb3ReadOnly,
} = new ExternalStore<MulticallProvider<Web3Provider | JsonRpcProvider>>()

export const getUserNonce = async (userAddress: string): Promise<number> => {
  const web3 = getWeb3ReadOnly()
  if (!web3) return -1
  try {
    return await web3.getTransactionCount(userAddress, 'pending')
  } catch (error) {
    return Promise.reject(error)
  }
}

export const isSmartContract = async (provider: JsonRpcProvider, address: string): Promise<boolean> => {
  const code = await provider.getCode(address)

  return code !== EMPTY_DATA
}
